/**
 * Where a detail page sends the user when they back out of it.
 *
 * A detail page is reachable from several lists (the all-invoices table, a
 * client's own invoices, Payments, the dashboard), so the page itself cannot
 * know which one to return to. Links into it carry their origin as router
 * state; the detail page reads it and falls back to its canonical list when
 * there is none — a pasted link, a refresh into a fresh history entry, or a
 * caller that does not pass one.
 */
import { useLocation } from 'react-router-dom';

export interface BackTo {
  /** Internal path to return to, query string included. */
  to: string;
  /** The link's text, without the arrow: "All invoices", "Back to Zealthy". */
  label: string;
}

/** Router state for a link into a detail page: `state={backState(...)}`. */
export const backState = (from: BackTo) => ({ from });

/**
 * Router state is user-controllable (history.state survives reloads and can be
 * crafted), so treat it as untrusted input: take it only when it is a complete
 * BackTo naming an in-app path. Rejecting "//host" as well as absolute URLs
 * keeps the link from becoming an off-site redirect.
 */
const isInternalPath = (to: string): boolean => to.startsWith('/') && !to.startsWith('//');

export function useBackTo(fallback: BackTo): BackTo {
  const from = (useLocation().state as { from?: Partial<BackTo> } | null)?.from;
  if (!from || typeof from.to !== 'string' || typeof from.label !== 'string') return fallback;
  if (!isInternalPath(from.to) || from.label.trim() === '') return fallback;
  return { to: from.to, label: from.label };
}
